import React, { useLayoutEffect, useRef, useState } from "react"
import { Box, ButtonBase, Divider, IconButton, Menu, MenuItem, Typography } from "@mui/material"
import { alpha, keyframes } from "@mui/material/styles"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"


const rise = keyframes({
	from: { opacity: 0, transform: 'translate(-50%, 12px)' },
	to: { opacity: 1, transform: 'translate(-50%, 0)' },
})

// Horizontal centre of the table card the bar belongs to, kept up to date when the card or the window resize
function useCardCentre(anchorRef) {
	const [centre, setCentre] = useState(null)

	useLayoutEffect(() => {
		const card = anchorRef.current?.closest('[class*="MUIDataTable-paper"]') || anchorRef.current?.parentElement
		if (!card) return
		const measure = () => {
			const rect = card.getBoundingClientRect()
			setCentre(rect.left + rect.width / 2)
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(card)
		window.addEventListener('resize', measure)
		return () => {
			observer.disconnect()
			window.removeEventListener('resize', measure)
		}
	}, [])

	return centre
}


// Floating bar shown while table rows are selected: count, the actions for the selection and a × to clear it.
// It stays in view at the bottom of the screen, centred on its table, and never moves the table.
export default function AppSelectionBar({ count, onClear, children, ...props }) {
	const anchorRef = useRef(null)
	const centre = useCardCentre(anchorRef)

	return <Box ref={anchorRef} className="app-selection-bar">
		{centre !== null && <Box
			role="toolbar"
			aria-label={`${count} selected`}
			{...props}
			sx={{
				position: 'fixed',
				bottom: 24,
				left: centre,
				transform: 'translateX(-50%)',
				zIndex: 1050,
				display: 'flex',
				alignItems: 'center',
				gap: 0.5,
				pl: 2,
				pr: 1,
				py: 1,
				borderRadius: '14px',
				// the navy of the table headers it works on
				backgroundColor: dsColorNew.tableHead,
				boxShadow: `0 16px 40px ${alpha(dsColorNew.navy, 0.35)}`,
				whiteSpace: 'nowrap',
				animation: `${rise} .18s ease-out`,
				...props?.sx,
			}}
		>
			<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mr: 0.5 }}>
				<Box sx={{
					minWidth: 24, height: 24, px: '7px', borderRadius: '12px',
					display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
					backgroundColor: dsColorNew.white, color: dsColorNew.tableHead, fontSize: 12, fontWeight: 600,
				}}>
					{count}
				</Box>
				<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.white }}>selected</Typography>
			</Box>
			<AppSelectionBarDivider/>
			{children}
			<IconButton aria-label="Clear selection" onClick={onClear} size="small" sx={{
				ml: 0.5, width: 30, height: 30, borderRadius: '8px', color: alpha(dsColorNew.white, 0.6),
				'&:hover': { backgroundColor: alpha(dsColorNew.white, 0.1), color: dsColorNew.white },
			}}>
				<IconSeeker icon="xmark" sx={{ fontSize: 14 }}/>
			</IconButton>
		</Box>}
	</Box>
}

export function AppSelectionBarDivider() {
	return <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75, borderColor: alpha(dsColorNew.white, 0.18) }}/>
}

const buttonSx = danger => ({
	height: 34,
	px: 1.375,
	gap: 0.875,
	borderRadius: '9px',
	fontSize: 13,
	fontWeight: 500,
	fontFamily: 'inherit',
	color: danger ? dsColorNew.dangerBorder : alpha(dsColorNew.white, 0.88),
	'& .bar-icon': { fontSize: 13, color: danger ? dsColorNew.dangerBorder : alpha(dsColorNew.white, 0.6) },
	'&:hover, &.Mui-focusVisible': { backgroundColor: alpha(dsColorNew.white, 0.1) },
	'&.Mui-disabled': { opacity: 0.5 },
})

export function AppSelectionBarButton({ icon, label, danger = false, ...props }) {
	return <ButtonBase {...props} sx={{ ...buttonSx(danger), ...props?.sx }}>
		<IconSeeker icon={icon} className="bar-icon"/>
		{label}
	</ButtonBase>
}

// A bar button that opens a list of actions above it: [{ icon, label, onClick, ...props }]
export function AppSelectionBarMenu({ icon, label, items = [], ...props }) {
	const [anchor, setAnchor] = useState(null)

	return <>
		<ButtonBase
			aria-haspopup="menu"
			aria-expanded={!!anchor}
			onClick={e => setAnchor(e.currentTarget)}
			{...props}
			sx={{ ...buttonSx(false), ...props?.sx }}
		>
			<IconSeeker icon={icon} className="bar-icon"/>
			{label}
			<IconSeeker icon={anchor ? 'chevron-down' : 'chevron-up'} className="bar-icon" sx={{ fontSize: '9px !important' }}/>
		</ButtonBase>
		<Menu
			anchorEl={anchor}
			open={!!anchor}
			onClose={() => setAnchor(null)}
			anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
			transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			slotProps={{ paper: { sx: { mt: -1, minWidth: 280, p: 0.75 } } }}
			MenuListProps={{ sx: { p: 0 } }}
		>
			{items.map(({ icon: itemIcon, label: itemLabel, hint, onClick, ...itemProps }) => <MenuItem
				key={itemLabel}
				onClick={() => {
					setAnchor(null)
					onClick?.()
				}}
				{...itemProps}
				sx={{ gap: 1.25, px: 1.25, py: 1, borderRadius: '8px', color: dsColorNew.textDark, '&:hover': { backgroundColor: dsColorNew.hoverBgLight } }}
			>
				<IconSeeker icon={itemIcon} sx={{ width: 18, fontSize: 15, color: dsColorNew.textMuted }}/>
				{itemLabel}
				{!!hint && <Typography component="span" sx={{ ml: 'auto', pl: 2, fontSize: 12, color: dsColorNew.textSubtle }}>{hint}</Typography>}
			</MenuItem>)}
		</Menu>
	</>
}
